import { useEffect, useState, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useFiscalStore } from "@/store/fiscal-store";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { fmtDate, fmtBRL, fmtNum } from "@/lib/format";
import { 
  ShieldCheck, ShieldOff, UserCog, Trash2, Plus, RefreshCw, Copy, KeyRound, Users,
  CreditCard, TrendingUp, Sparkles, Settings, Globe, Activity, Percent, Save, Info, Scale, Database,
  Zap, Layers, Search, Filter, Calculator, FileCheck, FileCode, AlertTriangle, Download,
  ToggleLeft, ToggleRight, BookOpen, Edit2, CheckCircle2
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { AuditSeverity, AuditCategory } from "@/lib/fiscal-types";

/* ───────────── Interfaces ───────────── */

interface ProfileRow {
  id: string;
  email: string;
  display_name: string | null;
  is_active: boolean;
  created_at: string;
  roles: string[];
}

interface InviteRow {
  id: string;
  code: string;
  is_active: boolean;
  max_uses: number | null;
  uses_count: number;
  expires_at: string | null;
  note: string | null;
  plan_id: string | null;
  created_at: string;
}

interface PlanConfig {
  id: string;
  name: string;
  description: string;
  price_monthly: number;
  price_annual: number;
  features: string[];
  icon_name: string;
  is_recommended: boolean;
  badge_text: string | null;
  is_active: boolean;
  display_order: number;
  user_limit: number;
  asaas_link_id_monthly?: string;
  asaas_link_url_monthly?: string;
  asaas_link_id_annual?: string;
  asaas_link_url_annual?: string;
}

interface SubscriptionWithProfile {
  id: string;
  user_id: string;
  plan: string;
  status: string;
  current_period_end: string | null;
  created_at: string;
  profiles: {
    email: string;
    display_name: string | null;
  };
}

interface RegraCustomizada {
  id: string;
  nome: string;
  descricao: string;
  campo: string;
  condicao: string;
  severidade: AuditSeverity;
  ativa: boolean;
}

/* ───────────── Constantes ───────────── */

const ALIQUOTAS_ICMS_PADRAO: Record<string, number> = {
  AC: 17, AL: 19, AP: 18, AM: 20, BA: 20, CE: 20, DF: 20, ES: 17,
  GO: 17, MA: 22, MT: 17, MS: 17, MG: 18, PA: 19, PB: 18, PR: 19,
  PE: 20.5, PI: 21, RJ: 22, RN: 18, RS: 17, RO: 17.5, RR: 17,
  SC: 17, SP: 18, SE: 19, TO: 20,
};

const PARAMETROS_BASE = [
  { key: "tolerancia_divergencia", label: "Tolerância de divergência (R$)", value: "0.01", tipo: "number", desc: "Diferença mínima para considerar uma divergência de valor." },
  { key: "limite_zip", label: "Limite máximo do ZIP (MB)", value: "500", tipo: "number", desc: "Tamanho máximo permitido para o arquivo ZIP de upload." },
  { key: "limite_xml_individual", label: "Limite XML individual (MB)", value: "10", tipo: "number", desc: "Tamanho máximo de cada XML dentro do ZIP." },
  { key: "max_xmls_por_lote", label: "Máx. XMLs por lote", value: "5000", tipo: "number", desc: "Quantidade máxima de XMLs processados por lote." },
  { key: "alerta_valor_alto", label: "Alerta valor alto (R$)", value: "10000", tipo: "number", desc: "Notas acima deste valor sem ICMS serão sinalizadas." },
];

const REGRAS_SISTEMA = [
  { code: "R1", layer: 1, title: "Hash SHA-256 do XML original", severity: "INFO" as AuditSeverity, category: "INTEGRIDADE" as AuditCategory, desc: "Cada XML recebe hash SHA-256. Duplicatas byte-a-byte são ignoradas.", status: "ATIVA" as const },
  { code: "R4", layer: 2, title: "vProd = qCom × vUnCom", severity: "MEDIA" as AuditSeverity, category: "MATEMATICA" as AuditCategory, desc: "Valida o produto da quantidade pela unitário com vProd (centavos).", status: "ATIVA" as const },
  { code: "R5", layer: 2, title: "Totais ICMSTot vs soma dos itens", severity: "MEDIA" as AuditSeverity, category: "MATEMATICA" as AuditCategory, desc: "vProd, vICMS, vST, vPIS, vCOFINS, vIPI batem com a soma item-a-item.", status: "ATIVA" as const },
  { code: "R16", layer: 2, title: "Totais IBSCBSTot vs soma dos itens", severity: "ALTA" as AuditSeverity, category: "MATEMATICA" as AuditCategory, desc: "Valida se os totais de IBS e CBS (Reforma) batem com a soma dos itens.", status: "ATIVA" as const },
  { code: "R6", layer: 2, title: "Fórmula oficial do vNF (MOC 7.0 + Reforma)", severity: "ALTA" as AuditSeverity, category: "MATEMATICA" as AuditCategory, desc: "vNF = vProd + vFrete + vST + vIBS + vCBS + vServ − vDesc − vICMSDeson (NT 2023.004).", status: "ATIVA" as const },
  { code: "R7", layer: 3, title: "CST × valores ICMS", severity: "ALTA" as AuditSeverity, category: "TRIBUTARIA" as AuditCategory, desc: "CST 00/10/20/70/90 exigem vBC, pICMS, vICMS > 0. CST 40/41/50/51 exigem vICMS = 0.", status: "ATIVA" as const },
  { code: "R8", layer: 3, title: "CFOP × tipo de operação (tpNF)", severity: "MEDIA" as AuditSeverity, category: "TRIBUTARIA" as AuditCategory, desc: "CFOPs 1/2/3 são entrada (tpNF=0); 5/6/7 são saída (tpNF=1).", status: "ATIVA" as const },
  { code: "R9", layer: 3, title: "Ambiente vs protocolo", severity: "CRITICA" as AuditSeverity, category: "PROTOCOLO" as AuditCategory, desc: "tpAmb=1 exige nProt e dhRecbto. tpAmb=2 = HOMOLOGAÇÃO.", status: "ATIVA" as const },
  { code: "R10", layer: 3, title: "Duplicidade por chave de acesso", severity: "ALTA" as AuditSeverity, category: "INTEGRIDADE" as AuditCategory, desc: "Detecta XMLs distintos com mesma chave de 44 dígitos.", status: "ATIVA" as const },
  { code: "R11", layer: 4, title: "Valor alto com imposto zero", severity: "MEDIA" as AuditSeverity, category: "COERENCIA" as AuditCategory, desc: "vNF > R$ 10.000 com ICMS = 0 e sem CST de isenção é flagado.", status: "ATIVA" as const },
  { code: "R13", layer: 4, title: "Coerência de transporte", severity: "MEDIA" as AuditSeverity, category: "COERENCIA" as AuditCategory, desc: "modFrete 0/1/2 exige transportadora, veículo ou volumes. Ignora transporte próprio (3/4).", status: "ATIVA" as const },
  { code: "R14", layer: 4, title: "Soma de pagamentos vs vNF", severity: "ALTA" as AuditSeverity, category: "COERENCIA" as AuditCategory, desc: "Σ vPag − vTroco = vNF. Considera margem de 10 centavos e ignora notas de ajuste/complementar.", status: "ATIVA" as const },
  { code: "R15", layer: 4, title: "IBS/CBS (Reforma Tributária)", severity: "ALTA" as AuditSeverity, category: "TRIBUTARIA" as AuditCategory, desc: "vIBS = vIBSUF + vIBSMun conforme partilha da Reforma Tributária.", status: "ATIVA" as const },
  { code: "R17", layer: 4, title: "Monitoramento de Transição", severity: "INFO" as AuditSeverity, category: "COERENCIA" as AuditCategory, desc: "Sinaliza incidência simultânea de PIS/COFINS e IBS/CBS (Regra de Transição 2026).", status: "ATIVA" as const },
  { code: "R2", layer: 1, title: "Validação de assinatura digital X509", severity: "CRITICA" as AuditSeverity, category: "ESTRUTURA" as AuditCategory, desc: "Verificação da cadeia ICP-Brasil. Requer Edge Function com OpenSSL.", status: "PENDENTE_BACKEND" as const },
  { code: "R3", layer: 1, title: "Validação de schema XSD oficial", severity: "ALTA" as AuditSeverity, category: "ESTRUTURA" as AuditCategory, desc: "Validação contra procNFe_v4.00.xsd da SEFAZ. Requer parser XSD server-side.", status: "PENDENTE_BACKEND" as const },
  { code: "R12", layer: 4, title: "Padrão anômalo por emitente", severity: "MEDIA" as AuditSeverity, category: "COERENCIA" as AuditCategory, desc: "Compara médias históricas de ICMS/PIS/COFINS por emitente. Requer persistência.", status: "PENDENTE_BACKEND" as const },
];

const SEV_COLORS: Record<AuditSeverity, string> = {
  CRITICA: "#F87171", ALTA: "#FBA94C", MEDIA: "#FCD34D", BAIXA: "#FBBF24", INFO: "#33CCFF",
};

const CAT_ICONS: Record<AuditCategory, any> = {
  INTEGRIDADE: FileCheck,
  MATEMATICA: Calculator,
  TRIBUTARIA: TrendingUp,
  PROTOCOLO: ShieldCheck,
  COERENCIA: AlertTriangle,
  ESTRUTURA: FileCode,
};

/* ───────────── Helpers ───────────── */

function randomCode(len = 8) {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

/* ───────────── Página Principal ───────────── */

export default function AdminPage() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");

  // State Global sincronizado
  const [regrasAtivas, setRegrasAtivas] = useState<Record<string, boolean>>({});
  const [params, setParams] = useState<Record<string, string>>({});
   const [aliquotas, setAliquotas] = useState<Record<string, number>>({});
   const [sidebarInfo, setSidebarInfo] = useState({
     version: "v2.4.0 · SPED Fiscal 2026",
     dpo_name: "Encarregado de Dados (DPO)",
     dpo_email: "privacidade@extratorfiscal.app",
     warning: "Atenção: Ferramenta auxiliar. A tomada de decisões tributárias exige a validação por um Profissional Contábil (CRC)."
   });

  async function loadGlobalSettings() {
    setLoading(true);
    try {
      const { data, error } = await supabase.from("app_settings").select("*");
      if (error) throw error;

      const settings: Record<string, any> = {};
      data.forEach(s => settings[s.key] = s.value);

      // Parse das regras
      if (settings.rules_config) {
        setRegrasAtivas(JSON.parse(settings.rules_config));
      } else {
        setRegrasAtivas(Object.fromEntries(REGRAS_SISTEMA.map(r => [r.code, r.status === "ATIVA"])));
      }

      // Parse dos parâmetros
      if (settings.fiscal_params) {
        setParams(JSON.parse(settings.fiscal_params));
      } else {
        setParams(Object.fromEntries(PARAMETROS_BASE.map(p => [p.key, p.value])));
      }

      // Parse das alíquotas
      if (settings.icms_aliquotas) {
        setAliquotas(JSON.parse(settings.icms_aliquotas));
       } else {
         setAliquotas({ ...ALIQUOTAS_ICMS_PADRAO });
       }
 
       // Parse da sidebar
       if (settings.sidebar_info) {
         setSidebarInfo(JSON.parse(settings.sidebar_info));
       }

    } catch (e: any) {
      toast({ title: "Erro ao carregar configurações", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  async function saveSetting(key: string, value: any) {
    const { error } = await supabase.from("app_settings").upsert({ 
      key, 
      value: JSON.stringify(value),
      updated_at: new Date().toISOString()
    });
    if (error) {
      toast({ title: "Erro ao salvar", description: error.message, variant: "destructive" });
      return false;
    }
    toast({ title: "Configuração sincronizada", description: "As mudanças já estão ativas para todos os usuários." });
    return true;
  }

  useEffect(() => { 
    document.title = "Administração — Extrator Fiscal";
    loadGlobalSettings();
  }, []);

  if (loading) {
    return (
      <div className="h-[60vh] flex flex-col items-center justify-center gap-4">
        <RefreshCw className="size-8 text-blue-500 animate-spin" />
        <p className="text-white/40 font-bold animate-pulse">Sincronizando configurações globais...</p>
      </div>
    );
  }

  return (
    <div className="px-6 py-6 lg:px-10 lg:py-8 max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-white/5 pb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="size-5 text-blue-500" />
            <span className="text-[10px] font-bold text-blue-500 uppercase tracking-[0.2em]">Painel de Controle</span>
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight">Administração</h1>
          <p className="text-sm text-white/50 mt-1">Gestão centralizada de usuários, assinaturas, parâmetros fiscais e regras do sistema.</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadGlobalSettings} className="bg-white/5 border-white/10 h-10">
          <RefreshCw className="size-4 mr-2" /> Forçar Sincronização
        </Button>
      </header>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-white/5 border border-white/10 p-1 flex flex-wrap h-auto">
          <TabsTrigger value="users" className="gap-2 px-4 py-2"><Users className="size-4" /> Usuários</TabsTrigger>
          <TabsTrigger value="invites" className="gap-2 px-4 py-2"><KeyRound className="size-4" /> Convites</TabsTrigger>
          <TabsTrigger value="finance" className="gap-2 px-4 py-2"><CreditCard className="size-4" /> Financeiro</TabsTrigger>
          <TabsTrigger value="params" className="gap-2 px-4 py-2"><Settings className="size-4" /> Parâmetros</TabsTrigger>
          <TabsTrigger value="rules" className="gap-2 px-4 py-2"><Zap className="size-4" /> Regras</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <UsersTab />
        </TabsContent>

        <TabsContent value="invites" className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <InvitesTab />
        </TabsContent>

        <TabsContent value="finance" className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <FinanceTab />
        </TabsContent>

        <TabsContent value="params" className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <ParamsTab 
            params={params} 
            setParams={setParams} 
            aliquotas={aliquotas} 
            setAliquotas={setAliquotas} 
            sidebarInfo={sidebarInfo}
            setSidebarInfo={setSidebarInfo}
            onSave={saveSetting} 
          />
        </TabsContent>

        <TabsContent value="rules" className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
          <RulesTab regrasAtivas={regrasAtivas} setRegrasAtivas={setRegrasAtivas} onSave={saveSetting} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ───────────── ABA: USUÁRIOS ───────────── */

function UsersTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    try {
      const [{ data: profiles, error: pe }, { data: roles, error: re }] = await Promise.all([
        supabase.from("profiles").select("id,email,display_name,is_active,created_at").order("created_at", { ascending: false }),
        supabase.from("user_roles").select("user_id,role"),
      ]);
      if (pe || re) throw new Error(pe?.message ?? re?.message);
      
      const byUser = new Map<string, string[]>();
      (roles ?? []).forEach((r: any) => {
        const arr = byUser.get(r.user_id) ?? [];
        arr.push(r.role);
        byUser.set(r.user_id, arr);
      });
      setRows((profiles ?? []).map((p: any) => ({ ...p, roles: byUser.get(p.id) ?? [] })));
    } catch (e: any) {
      toast({ title: "Erro ao carregar", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggleActive(row: ProfileRow) {
    const { error } = await supabase.from("profiles").update({ is_active: !row.is_active }).eq("id", row.id);
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    toast({ title: row.is_active ? "Usuário desativado" : "Usuário ativado" });
    load();
  }

  async function deleteUser(row: ProfileRow) {
    const { data, error } = await supabase.functions.invoke("admin-delete-user", { body: { user_id: row.id } });
    if (error || (data as any)?.error) return toast({ title: "Erro ao apagar", description: error?.message ?? (data as any)?.error, variant: "destructive" });
    toast({ title: "Usuário apagado" });
    load();
  }

  return (
    <Card className="glass-card overflow-hidden border-white/10">
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <h2 className="text-sm font-bold flex items-center gap-2"><Users className="size-4 text-blue-400" /> Lista de Usuários</h2>
        <Button size="sm" variant="outline" onClick={load} className="h-8 bg-white/5"><RefreshCw className={cn("size-3.5 mr-1", loading && "animate-spin")} /> Sincronizar</Button>
      </div>
      <Table>
        <TableHeader className="bg-white/5 uppercase font-black text-[9px] tracking-widest">
          <TableRow className="border-white/10">
            <TableHead className="text-white/40">Usuário</TableHead>
            <TableHead className="text-white/40">Roles</TableHead>
            <TableHead className="text-white/40">Status</TableHead>
            <TableHead className="text-white/40">Criado em</TableHead>
            <TableHead className="text-white/40 text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? <TableRow><TableCell colSpan={5} className="text-center py-10 opacity-50">Carregando...</TableCell></TableRow> :
           rows.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 opacity-50">Nenhum usuário encontrado</TableCell></TableRow> :
           rows.map(r => (
            <TableRow key={r.id} className="border-white/5 hover:bg-white/5 transition-all duration-300 group">
              <TableCell className="py-4">
                <div className="flex flex-col">
                  <span className="font-bold text-white group-hover:text-blue-400 transition-colors">{r.display_name || "—"}</span>
                  <span className="text-[10px] text-white/30 font-medium">{r.email}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex gap-1.5">
                  {r.roles.map(role => (
                    <Badge key={role} className={cn(
                      "text-[9px] font-black uppercase tracking-tighter px-1.5 py-0.5",
                      role === 'admin' 
                        ? "bg-blue-500/20 text-blue-400 border-blue-500/30" 
                        : "bg-white/5 text-white/40 border-white/10"
                    )}>{role}</Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell>
                <Badge className={cn(
                  "text-[9px] font-black uppercase",
                  r.is_active 
                    ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" 
                    : "bg-red-500/20 text-red-400 border-red-500/30"
                )}>{r.is_active ? "Ativo" : "Inativo"}</Badge>
              </TableCell>
              <TableCell className="text-[10px] font-medium text-white/20">{fmtDate(r.created_at)}</TableCell>
              <TableCell className="text-right space-x-1">
                <Button size="sm" variant="ghost" disabled={user?.id === r.id} onClick={() => toggleActive(r)} className="hover:bg-white/5 text-white/40 hover:text-white transition-all">
                  {r.is_active ? <ShieldOff className="size-4" /> : <ShieldCheck className="size-4" />}
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" disabled={user?.id === r.id} className="text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-all">
                      <Trash2 className="size-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="glass-card border-white/20 text-white backdrop-blur-2xl bg-slate-950/80">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-xl font-black">Apagar usuário?</AlertDialogTitle>
                      <AlertDialogDescription className="text-white/50">Esta ação remove permanentemente <span className="text-white font-bold">{r.email}</span> do sistema.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="mt-4">
                      <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteUser(r)} className="bg-red-600 hover:bg-red-500 text-white font-bold">Confirmar Exclusão</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/* ───────────── ABA: CONVITES ───────────── */

function InvitesTab() {
  const [rows, setRows] = useState<InviteRow[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<InviteRow | null>(null);
  const [open, setOpen] = useState(false);
  const [inviteUsersMap, setInviteUsersMap] = useState<Record<string, any[]>>({});

  async function load() {
    setLoading(true);
    try {
      const [{ data: invites }, { data: plansRes }, { data: profiles }] = await Promise.all([
        supabase.from("invites").select("*").order("created_at", { ascending: false }),
        supabase.from("plans_config").select("id, name, user_limit").order("display_order", { ascending: true }),
        supabase.from("profiles").select("display_name, email, raw_user_meta_data")
      ]);
      const map: Record<string, any[]> = {};
      profiles?.forEach(p => {
        const code = p.raw_user_meta_data?.invite_code || (p as any).invite_code;
        if (code) { if (!map[code]) map[code] = []; map[code].push(p); }
      });
      setInviteUsersMap(map);
      setRows((invites ?? []) as InviteRow[]);
      setPlans(plansRes ?? []);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function save() {
    if (!editing) return;
    const payload = {
      code: editing.code.trim().toUpperCase(),
      is_active: editing.is_active,
      max_uses: editing.max_uses ?? null,
      plan_id: editing.plan_id && editing.plan_id !== "" ? editing.plan_id : null,
      note: editing.note ?? null,
    };
    const q = editing.id ? supabase.from("invites").update(payload).eq("id", editing.id) : supabase.from("invites").insert(payload);
    const { error } = await q;
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    toast({ title: "Convite salvo" });
    setOpen(false); load();
  }

  return (
    <Card className="glass-card border-white/10 overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <h2 className="text-sm font-bold flex items-center gap-2"><KeyRound className="size-4 text-sky-400" /> Gestão de Convites</h2>
        <Button size="sm" onClick={() => { setEditing({ id: "", code: randomCode(), is_active: true, max_uses: null, uses_count: 0, expires_at: null, note: "", plan_id: null, created_at: "" }); setOpen(true); }} className="h-8 bg-sky-600 hover:bg-sky-500"><Plus className="size-3.5 mr-1" /> Novo Convite</Button>
      </div>
      <Table>
        <TableHeader className="bg-white/5 uppercase font-black text-[9px] tracking-widest">
          <TableRow className="border-white/10">
            <TableHead className="text-white/40">Código / Plano</TableHead>
            <TableHead className="text-white/40 text-center">Usos / Limite</TableHead>
            <TableHead className="text-white/40 text-center">Status</TableHead>
            <TableHead className="text-white/40">Nota</TableHead>
            <TableHead className="text-white/40 text-right">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? <TableRow><TableCell colSpan={5} className="text-center py-10 opacity-50">Carregando...</TableCell></TableRow> :
           rows.map(r => {
             const plan = plans.find(p => p.id === r.plan_id);
             const users = inviteUsersMap[r.code] || [];
             const limit = r.max_uses || plan?.user_limit || 0;
             return (
              <TableRow key={r.id} className="border-white/5 hover:bg-white/5 transition-colors">
                <TableCell>
                  <div className="flex flex-col"><span className="font-mono font-bold text-white tracking-wider">{r.code}</span><Badge variant="outline" className="w-fit text-[8px] px-1 py-0 border-white/10 text-white/40">{plan?.name || "Padrão"}</Badge></div>
                </TableCell>
                <TableCell className="text-center font-bold text-white">{users.length} <span className="text-white/20">/ {limit || '∞'}</span></TableCell>
                <TableCell className="text-center"><Badge className={r.is_active ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-white/10 text-white/40"}>{r.is_active ? "Ativo" : "Pausado"}</Badge></TableCell>
                <TableCell className="text-xs text-white/40 italic">{r.note || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEditing(r); setOpen(true); }}><UserCog className="size-4" /></Button>
                </TableCell>
              </TableRow>
             );
           })}
        </TableBody>
      </Table>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass-card border-white/20 text-white backdrop-blur-2xl bg-slate-950/80 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <KeyRound className="size-5 text-sky-400" />
              {editing?.id ? "Editar" : "Novo"} Convite
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-5 py-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Código de Acesso</Label>
                <div className="flex gap-2">
                  <Input value={editing.code} onChange={e => setEditing({...editing, code: e.target.value.toUpperCase()})} className="bg-white/5 border-white/10 font-mono font-bold tracking-widest text-sky-400 h-11" />
                  <Button variant="outline" size="icon" onClick={() => setEditing({...editing, code: randomCode()})} className="bg-white/5 border-white/10 h-11 w-11"><RefreshCw className="size-4" /></Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Vincular a Plano</Label>
                <Select value={editing.plan_id || ""} onValueChange={v => setEditing({...editing, plan_id: v || null})}>
                  <SelectTrigger className="bg-white/5 border-white/10 text-white h-11">
                    <SelectValue placeholder="Plano Padrão" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A1229] border-white/20 text-white backdrop-blur-xl">
                    <SelectItem value="">Plano Padrão</SelectItem>
                    {plans.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Nota Administrativa</Label>
                <Input value={editing.note || ""} onChange={e => setEditing({...editing, note: e.target.value})} placeholder="Ex: Parceria, Promoção..." className="bg-white/5 border-white/10 h-11" />
              </div>

              <div className="flex items-center gap-3 bg-white/5 p-3 rounded-xl border border-white/5">
                <Switch checked={editing.is_active} onCheckedChange={v => setEditing({...editing, is_active: v})} />
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-white">Convite {editing.is_active ? "Ativo" : "Pausado"}</span>
                  <span className="text-[10px] text-white/40">Define se o código pode ser usado agora.</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={save} className="bg-sky-600 hover:bg-sky-500 w-full h-11 font-bold text-white shadow-[0_0_20px_rgba(14,165,233,0.3)] transition-all">Salvar Convite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ───────────── ABA: FINANCEIRO ───────────── */

function FinanceTab() {
  const [subs, setSubs] = useState<SubscriptionWithProfile[]>([]);
  const [plans, setPlans] = useState<PlanConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [financeSubTab, setFinanceSubTab] = useState("subscribers");
  
  // Estado para edição de plano
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [annualDiscount, setAnnualDiscount] = useState(20);

  // Estado para edição de assinante
  const [isSubDialogOpen, setIsSubDialogOpen] = useState(false);
  const [editingSub, setEditingSub] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");

  async function loadData() {
    setLoading(true);
    try {
      const [subsRes, plansRes] = await Promise.all([
        supabase.from("subscriptions").select("*, profiles:user_id(email, display_name)").order("created_at", { ascending: false }),
        supabase.from("plans_config").select("*").order("display_order", { ascending: true }),
      ]);
      setSubs(subsRes.data as any || []);
      setPlans(plansRes.data as any || []);
    } finally { setLoading(false); }
  }

  useEffect(() => { loadData(); }, []);

  async function handleSavePlan() {
    if (!editingPlan) return;
    const { error } = await supabase.from("plans_config").upsert(editingPlan);
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    toast({ title: editingPlan.id ? "Plano atualizado" : "Plano criado" });
    setIsPlanDialogOpen(false);
    loadData();
  }

  async function handleDeletePlan(id: string) {
    if (!confirm("Tem certeza que deseja excluir este plano?")) return;
    const { error } = await supabase.from("plans_config").delete().eq("id", id);
    if (error) return toast({ title: "Erro", description: error.message, variant: "destructive" });
    toast({ title: "Plano excluído" });
    loadData();
  }

  async function handleUpdateSubscription() {
    if (!editingSub) return;
    const { error } = await supabase.from("subscriptions").update({
      plan: editingSub.plan,
      status: editingSub.status,
      current_period_end: editingSub.current_period_end
    }).eq("id", editingSub.id);

    if (error) return toast({ title: "Erro ao atualizar", description: error.message, variant: "destructive" });
    toast({ title: "Assinatura atualizada" });
    setIsSubDialogOpen(false);
    loadData();
  }

  const filteredSubs = subs.filter(s => 
    s.profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.profiles?.display_name?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const stats = {
    mrr: subs.filter(s => s.status === 'active').reduce((acc, curr) => {
      const plan = plans.find(p => p.name.toLowerCase().includes(curr.plan.toLowerCase()));
      return acc + (plan?.price_monthly || 0);
    }, 0),
    activeCount: subs.filter(s => s.status === 'active').length,
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="glass-card p-6 flex items-center gap-4 border-white/10">
          <div className="size-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center text-emerald-500 border border-emerald-500/20"><TrendingUp className="size-6" /></div>
          <div><p className="text-xs font-black uppercase tracking-widest text-white/40">MRR Ativo</p><p className="text-2xl font-black text-white">{fmtBRL(stats.mrr)}</p></div>
        </Card>
        <Card className="glass-card p-6 flex items-center gap-4 border-white/10">
          <div className="size-12 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-500 border border-blue-500/20"><Users className="size-6" /></div>
          <div><p className="text-xs font-black uppercase tracking-widest text-white/40">Assinantes</p><p className="text-2xl font-black text-white">{stats.activeCount}</p></div>
        </Card>
        <Card className="glass-card p-6 flex items-center gap-4 border-white/10">
          <div className="size-12 rounded-2xl bg-purple-500/10 flex items-center justify-center text-purple-500 border border-purple-500/20"><Sparkles className="size-6" /></div>
          <div><p className="text-xs font-black uppercase tracking-widest text-white/40">Planos Ativos</p><p className="text-2xl font-black text-white">{plans.filter(p => p.is_active).length}</p></div>
        </Card>
      </div>

      <Tabs value={financeSubTab} onValueChange={setFinanceSubTab} className="space-y-6">
        <TabsList className="bg-white/5 border border-white/10 p-1">
          <TabsTrigger value="subscribers" className="gap-2"><Users className="size-4" /> Assinantes</TabsTrigger>
          <TabsTrigger value="manage_plans" className="gap-2"><Sparkles className="size-4" /> Gestão de Planos</TabsTrigger>
        </TabsList>

        <TabsContent value="subscribers" className="space-y-6">
          <Card className="glass-card overflow-hidden border-white/10">
            <div className="p-4 border-b border-white/10 flex gap-4 items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-white/30" />
                <Input 
                  placeholder="Buscar usuário..." 
                  className="pl-10 bg-white/5 border-white/10" 
                  value={searchTerm} 
                  onChange={e => setSearchTerm(e.target.value)} 
                />
              </div>
              <Button variant="ghost" size="sm" onClick={loadData} className="h-10 px-4 bg-white/5 border border-white/10">
                <RefreshCw className={cn("size-3.5 mr-2", loading && "animate-spin")} /> Sincronizar
              </Button>
            </div>
            <Table>
              <TableHeader className="bg-white/5 uppercase font-black text-[9px] tracking-widest">
                <TableRow className="border-white/10">
                  <TableHead className="text-white/40">Cliente</TableHead>
                  <TableHead className="text-white/40 text-center">Plano</TableHead>
                  <TableHead className="text-white/40 text-center">Status</TableHead>
                  <TableHead className="text-white/40">Vencimento</TableHead>
                  <TableHead className="text-white/40 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? <TableRow><TableCell colSpan={5} className="text-center py-10 opacity-50">Carregando...</TableCell></TableRow> :
                 filteredSubs.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-10 opacity-50">Nenhuma assinatura encontrada</TableCell></TableRow> :
                 filteredSubs.map(s => (
                  <TableRow key={s.id} className="border-white/5 hover:bg-white/5 group">
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-bold text-white">{s.profiles?.display_name || "—"}</span>
                        <span className="text-xs text-white/40">{s.profiles?.email}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className="uppercase text-[9px] border-white/10">{s.plan}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={cn(
                        "text-[9px] font-black uppercase",
                        s.status === 'active' ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" : "bg-white/10 text-white/40"
                      )}>{s.status}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-white/40">{s.current_period_end ? fmtDate(s.current_period_end) : '—'}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingSub({...s}); setIsSubDialogOpen(true); }}>
                        <Edit2 className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                 ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="manage_plans" className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={() => { setEditingPlan({ name: "", price_monthly: 0, price_annual: 0, features: [], is_active: true, display_order: plans.length + 1 }); setIsPlanDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-500">
              <Plus className="size-4 mr-2" /> Criar Novo Plano
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {plans.map(plan => (
              <Card key={plan.id} className={cn(
                "relative flex flex-col justify-between transition-all duration-500 border rounded-3xl p-7 shadow-sm",
                plan.is_active 
                  ? "bg-white border-slate-200 hover:border-primary/50 shadow-slate-200/50" 
                  : "bg-slate-50 border-slate-100 opacity-50 grayscale"
              )}>
                <div className="space-y-5">
                  <div className="flex justify-between items-start">
                    <div className="max-w-[70%]">
                      <h3 className="text-xl font-black text-slate-900 tracking-tight">{plan.name}</h3>
                      <p className="text-[10px] font-black uppercase tracking-widest text-primary mt-1">{plan.description || "Sem descrição"}</p>
                    </div>
                    {plan.is_recommended && (
                      <Badge className="bg-primary text-white border-none text-[8px] font-black px-2 py-0.5 shadow-md shadow-primary/20">
                        RECOMENDADO
                      </Badge>
                    )}
                  </div>

                  <div className="flex flex-col gap-1 py-3 border-y border-slate-100">
                    <div className="flex items-baseline gap-1 text-slate-900">
                      <span className="text-sm font-bold text-slate-400">R$</span>
                      <span className="text-3xl font-black">{fmtNum(plan.price_monthly)}</span>
                      <span className="text-xs font-bold text-slate-400 uppercase">/mês</span>
                    </div>
                    <div className="text-[10px] font-bold text-emerald-600 flex items-center gap-1.5 uppercase tracking-wide">
                      <Percent className="size-3" /> Anual: {fmtBRL(plan.price_annual)}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-primary/5 border border-primary/10 w-fit">
                      <Users className="size-3.5 text-primary" />
                      <span className="text-[10px] font-black text-primary uppercase tracking-widest">
                        Limite: {plan.user_limit} usuário(s)
                      </span>
                    </div>
                    
                    <ul className="space-y-2">
                      {plan.features?.slice(0, 4).map((f, i) => (
                        <li key={i} className="flex gap-2.5 items-center">
                          <div className="size-4 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0">
                            <CheckCircle2 className="size-2.5 text-emerald-500" />
                          </div>
                          <span className="text-[11px] text-slate-600 font-medium truncate">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex gap-3 mt-8">
                  <Button 
                    variant="outline" 
                    className="flex-1 h-11 bg-white border-slate-200 hover:border-primary/30 hover:bg-slate-50 text-slate-700 text-[10px] font-black uppercase tracking-[0.2em] rounded-xl transition-all shadow-sm" 
                    onClick={() => { 
                      setEditingPlan({...plan}); 
                      const discount = plan.price_monthly > 0 ? Math.round((1 - (plan.price_annual / (plan.price_monthly * 12))) * 100) : 0;
                      setAnnualDiscount(discount);
                      setIsPlanDialogOpen(true); 
                    }}
                  >
                    Editar Plano
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="size-11 rounded-xl text-red-400 hover:text-red-600 hover:bg-red-50" 
                    onClick={() => handleDeletePlan(plan.id)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* MODAL EDIÇÃO PLANO */}
      <Dialog open={isPlanDialogOpen} onOpenChange={setIsPlanDialogOpen}>
        <DialogContent className="glass-card border-white/20 text-white max-w-xl max-h-[90vh] overflow-y-auto backdrop-blur-2xl bg-slate-950/80">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <Sparkles className="size-5 text-blue-400" />
              {editingPlan?.id ? "Configurar Plano" : "Criar Novo Plano"}
            </DialogTitle>
          </DialogHeader>
          {editingPlan && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Nome do Plano</Label>
                  <Input value={editingPlan.name} onChange={e => setEditingPlan({...editingPlan, name: e.target.value})} className="bg-white/5 border-white/10 h-11" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Ícone (Lucide)</Label>
                  <Input value={editingPlan.icon_name} onChange={e => setEditingPlan({...editingPlan, icon_name: e.target.value})} className="bg-white/5 border-white/10 h-11" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Descrição Comercial</Label>
                <Input value={editingPlan.description || ""} onChange={e => setEditingPlan({...editingPlan, description: e.target.value})} className="bg-white/5 border-white/10 h-11" />
              </div>

              <div className="grid grid-cols-3 gap-4 p-4 bg-white/5 rounded-2xl border border-white/5">
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase text-blue-400 tracking-widest">Mensal (R$)</Label>
                  <Input type="number" step="0.01" value={editingPlan.price_monthly} onChange={e => {
                    const monthly = Number(e.target.value);
                    const annual = (monthly * 12) * (1 - annualDiscount / 100);
                    setEditingPlan({...editingPlan, price_monthly: monthly, price_annual: Number(annual.toFixed(2))});
                  }} className="bg-white/10 border-white/10 h-11 font-bold" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase text-emerald-400 tracking-widest">Desc. Anual (%)</Label>
                  <Input type="number" value={annualDiscount} onChange={e => {
                    const disc = Number(e.target.value);
                    setAnnualDiscount(disc);
                    const annual = (Number(editingPlan.price_monthly || 0) * 12) * (1 - disc / 100);
                    setEditingPlan({...editingPlan, price_annual: Number(annual.toFixed(2))});
                  }} className="bg-emerald-500/10 border-emerald-500/20 text-emerald-400 h-11 font-bold" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase text-blue-400 tracking-widest">Anual (R$)</Label>
                  <Input type="number" step="0.01" value={editingPlan.price_annual} onChange={e => setEditingPlan({...editingPlan, price_annual: Number(e.target.value)})} className="bg-white/10 border-white/10 h-11 font-bold" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Limite de Usuários</Label>
                  <Input type="number" value={editingPlan.user_limit} onChange={e => setEditingPlan({...editingPlan, user_limit: Number(e.target.value)})} className="bg-white/5 border-white/10 h-11" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Ordem de Exibição</Label>
                  <Input type="number" value={editingPlan.display_order} onChange={e => setEditingPlan({...editingPlan, display_order: Number(e.target.value)})} className="bg-white/5 border-white/10 h-11" />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Recursos Inclusos (Um por linha)</Label>
                <Textarea value={editingPlan.features?.join('\n')} onChange={e => setEditingPlan({...editingPlan, features: e.target.value.split('\n')})} className="bg-white/5 border-white/10 h-32 text-xs leading-relaxed" />
              </div>

              <div className="space-y-4 border-t border-white/10 pt-6">
                <h4 className="text-[10px] font-black text-blue-400 uppercase tracking-[0.2em] flex items-center gap-2">
                  <CreditCard className="size-4" /> Integração de Pagamento Asaas
                </h4>
                <div className="grid grid-cols-2 gap-x-4 gap-y-4 bg-white/5 p-4 rounded-2xl border border-white/5">
                  <div className="space-y-1"><Label className="text-[9px] text-white/30 uppercase">Link ID Mensal</Label><Input value={editingPlan.asaas_link_id_monthly || ""} onChange={e => setEditingPlan({...editingPlan, asaas_link_id_monthly: e.target.value})} className="bg-white/5 border-white/10 text-xs h-9" /></div>
                  <div className="space-y-1"><Label className="text-[9px] text-white/30 uppercase">URL Link Mensal</Label><Input value={editingPlan.asaas_link_url_monthly || ""} onChange={e => setEditingPlan({...editingPlan, asaas_link_url_monthly: e.target.value})} className="bg-white/5 border-white/10 text-xs h-9" /></div>
                  <div className="space-y-1"><Label className="text-[9px] text-white/30 uppercase">Link ID Anual</Label><Input value={editingPlan.asaas_link_id_annual || ""} onChange={e => setEditingPlan({...editingPlan, asaas_link_id_annual: e.target.value})} className="bg-white/5 border-white/10 text-xs h-9" /></div>
                  <div className="space-y-1"><Label className="text-[9px] text-white/30 uppercase">URL Link Anual</Label><Input value={editingPlan.asaas_link_url_annual || ""} onChange={e => setEditingPlan({...editingPlan, asaas_link_url_annual: e.target.value})} className="bg-white/5 border-white/10 text-xs h-9" /></div>
                </div>
              </div>

              <div className="flex items-center gap-3 bg-blue-500/10 p-3 rounded-xl border border-blue-500/20">
                <Switch checked={editingPlan.is_active} onCheckedChange={v => setEditingPlan({...editingPlan, is_active: v})} />
                <div className="flex flex-col">
                  <span className="text-sm font-bold text-blue-400">Plano Ativo para Vendas</span>
                  <span className="text-[10px] text-blue-400/60">Define se o plano aparecerá na página de Checkout.</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="sticky bottom-0 bg-slate-900/50 backdrop-blur-md pt-4 -mx-6 px-6">
            <Button onClick={handleSavePlan} className="bg-blue-600 hover:bg-blue-500 w-full h-12 font-black text-white shadow-[0_0_30px_rgba(37,99,235,0.4)]">SALVAR CONFIGURAÇÕES</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MODAL EDIÇÃO ASSINANTE */}
      <Dialog open={isSubDialogOpen} onOpenChange={setIsSubDialogOpen}>
        <DialogContent className="glass-card border-white/20 text-white backdrop-blur-2xl bg-slate-950/80">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <UserCog className="size-5 text-emerald-400" />
              Gerenciar Assinatura
            </DialogTitle>
          </DialogHeader>
          {editingSub && (
            <div className="space-y-5 py-4">
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Plano do Usuário</Label>
                <Select value={editingSub.plan} onValueChange={v => setEditingSub({...editingSub, plan: v})}>
                  <SelectTrigger className="bg-white/5 border-white/10 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A1229] border-white/20 text-white backdrop-blur-xl">
                    {plans.map(p => <SelectItem key={p.id} value={p.name.toLowerCase().replace("plano ", "")}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Status da Conta</Label>
                <Select value={editingSub.status} onValueChange={v => setEditingSub({...editingSub, status: v})}>
                  <SelectTrigger className="bg-white/5 border-white/10 h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A1229] border-white/20 text-white backdrop-blur-xl">
                    <SelectItem value="active">Ativa</SelectItem>
                    <SelectItem value="inactive">Inativa</SelectItem>
                    <SelectItem value="canceled">Cancelada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Vencimento da Cota</Label>
                <Input type="date" value={editingSub.current_period_end?.split('T')[0] || ''} onChange={e => setEditingSub({...editingSub, current_period_end: e.target.value ? new Date(e.target.value).toISOString() : null})} className="bg-white/5 border-white/10 h-11" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={handleUpdateSubscription} className="bg-emerald-600 hover:bg-emerald-500 w-full h-11 font-bold shadow-[0_0_20px_rgba(16,185,129,0.2)]">Salvar Alterações</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ───────────── ABA: PARÂMETROS ───────────── */

function ParamsTab({ params, setParams, aliquotas, setAliquotas, sidebarInfo, setSidebarInfo, onSave }: any) {
  const handleSave = async () => {
    const ok1 = await onSave('fiscal_params', params);
    const ok2 = await onSave('icms_aliquotas', aliquotas);
    const ok3 = await onSave('sidebar_info', sidebarInfo);
    if (ok1 && ok2 && ok3) {
      toast({ title: "Configurações globais salvas" });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6">
        <Card className="glass-card p-6 border-white/10 space-y-6">
          <div className="flex items-center gap-3 border-b border-white/5 pb-4">
            <div className="size-10 rounded-xl bg-blue-500/10 grid place-items-center"><Activity className="size-5 text-blue-500" /></div>
            <div><h3 className="font-bold text-white">Operação</h3><p className="text-xs text-white/40">Limites e validações do motor.</p></div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {PARAMETROS_BASE.map(p => (
              <div key={p.key} className="space-y-2">
                <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">{p.label}</Label>
                <Input value={params[p.key]} onChange={e => setParams({...params, [p.key]: e.target.value})} className="bg-white/5 border-white/10 h-10" />
                <p className="text-[10px] text-white/20 italic">{p.desc}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* NOVA SEÇÃO: SIDEBAR */}
        <Card className="glass-card p-6 border-white/10 space-y-6">
          <div className="flex items-center gap-3 border-b border-white/5 pb-4">
            <div className="size-10 rounded-xl bg-purple-500/10 grid place-items-center"><Globe className="size-5 text-purple-500" /></div>
            <div><h3 className="font-bold text-white">Rodapé da Barra Lateral</h3><p className="text-xs text-white/40">Informações institucionais e DPO.</p></div>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Versão / Título</Label>
              <Input value={sidebarInfo.version} onChange={e => setSidebarInfo({...sidebarInfo, version: e.target.value})} className="bg-white/5 border-white/10 h-10" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Encarregado (DPO)</Label>
              <Input value={sidebarInfo.dpo_name} onChange={e => setSidebarInfo({...sidebarInfo, dpo_name: e.target.value})} className="bg-white/5 border-white/10 h-10" />
            </div>
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">E-mail DPO</Label>
              <Input value={sidebarInfo.dpo_email} onChange={e => setSidebarInfo({...sidebarInfo, dpo_email: e.target.value})} className="bg-white/5 border-white/10 h-10" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-[10px] font-black uppercase text-white/40 tracking-widest">Aviso Legal</Label>
              <Textarea value={sidebarInfo.warning} onChange={e => setSidebarInfo({...sidebarInfo, warning: e.target.value})} className="bg-white/5 border-white/10 h-20 text-xs" />
            </div>
          </div>
          
          <Button onClick={handleSave} className="w-full bg-primary hover:bg-primary-600 text-white font-black uppercase tracking-widest text-xs h-12 shadow-glow">
            <Save className="size-4 mr-2" /> Salvar Todas as Configurações
          </Button>
        </Card>
      </div>

      <Card className="glass-card p-6 border-white/10 space-y-6">
        <div className="flex items-center gap-3 border-b border-white/5 pb-4">
          <div className="size-10 rounded-xl bg-amber-500/10 grid place-items-center"><Percent className="size-5 text-amber-500" /></div>
          <div><h3 className="font-bold text-white">ICMS por UF</h3><p className="text-xs text-white/40">Alíquotas modais estaduais.</p></div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(aliquotas).sort(([a], [b]) => a.localeCompare(b)).map(([uf, aliq]) => (
            <div key={uf} className="bg-white/5 p-2 rounded-lg border border-white/5 text-center">
              <span className="text-[9px] font-black text-white/40 block">{uf}</span>
              <input type="number" value={aliq} onChange={e => setAliquotas({...aliquotas, [uf]: Number(e.target.value)})} className="bg-transparent border-none text-xs font-bold text-white w-full text-center outline-none" />
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ───────────── ABA: REGRAS ───────────── */

function RulesTab({ regrasAtivas, setRegrasAtivas, onSave }: any) {
  const { dashboard } = useFiscalStore();
  
  const toggleRegra = async (code: string) => {
    const newState = { ...regrasAtivas, [code]: !regrasAtivas[code] };
    setRegrasAtivas(newState);
    await onSave('rules_config', newState);
  };

  const { porRegra } = dashboard?.auditSummary ?? { porRegra: {} as Record<string, number> };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="font-bold flex items-center gap-2"><Layers className="size-4 text-blue-400" /> Motor de Inspeção</h3>
        <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 uppercase text-[9px] px-2">Execução em Tempo Real</Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {REGRAS_SISTEMA.map(r => {
          const ativa = regrasAtivas[r.code] ?? false;
          const count = porRegra?.[r.code] ?? 0;
          const isPending = r.status === "PENDENTE_BACKEND";
          
          return (
            <Card key={r.code} className={cn(
              "glass-card p-5 border-white/10 flex items-start justify-between gap-4 transition-all duration-300",
              !ativa && "opacity-60 grayscale-[0.5]"
            )}>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[9px] font-black bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded tracking-tighter">{r.code}</span>
                  <span className="text-[9px] font-bold text-white/20 uppercase tracking-widest">Camada {r.layer}</span>
                  {isPending && <Badge variant="outline" className="text-[8px] border-amber-500/30 text-amber-500 bg-amber-500/5">NUVEM</Badge>}
                </div>
                <h4 className="font-bold text-sm text-white">{r.title}</h4>
                <p className="text-xs text-white/40 mt-1 line-clamp-2 leading-relaxed">{r.desc}</p>
                
                <div className="mt-4 flex items-center justify-between">
                  {count > 0 ? (
                    <div className="flex items-center gap-1.5 text-amber-500 text-[10px] font-bold">
                      <div className="size-1.5 rounded-full bg-amber-500 animate-pulse" /> 
                      {fmtNum(count)} indícios
                    </div>
                  ) : (
                    <span className="text-[9px] font-bold text-emerald-500/60 uppercase tracking-widest">Conforme</span>
                  )}
                </div>
              </div>
              
              <button 
                disabled={isPending}
                onClick={() => toggleRegra(r.code)} 
                className={cn(
                  "hover:scale-110 transition-transform mt-1",
                  isPending && "opacity-20 cursor-not-allowed"
                )}
              >
                {ativa ? (
                  <ToggleRight className="size-8 text-blue-500 drop-shadow-[0_0_8px_rgba(59,130,246,0.4)]" />
                ) : (
                  <ToggleLeft className="size-8 text-slate-500 dark:text-slate-600" />
                )}
              </button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
